import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import VideoPlayer from '@/components/VideoPlayer.vue'
  import CourseModules from '@/components/course/CourseModules.vue'
  import CourseDescription from '@/components/course/CourseDescription.vue'
  import PlaceholderShimmer from '@/components/common/PlaceholderShimmer.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import useIcons from '@/composables/useIcons'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useCourse from '@/composables/useCourse'
  import useVideo from '@/composables/useVideo'
  import { CourseModule } from '@/store/modules/course'
  import useSegment from '@/composables/useSegment'
  import TestimonialSlider from '@/components/course/TestimonialSlider.vue'
  import ProgressBar from '@/components/common/ProgressBar.vue'
  import CourseModuleStatusContainer from '@/components/course/CourseModuleStatusContainer.vue'
  import CourseModuleDuration from '@/components/course/CourseModuleDuration.vue'
  import FeedbackPriceSection from '@/components/course/FeedbackPriceSection.vue'
  import useBreakpoint from '@/composables/useBreakpoint'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import type {
    CourseItem,
    CoursePackage,
    LessonChangeLog,
    CourseModule as CourseModuleState,
  } from '@/services/interfaces/Course'
  import GoogleReviewWidget from '@/components/GoogleReviewWidget.vue'
  import { v4 as uuidv4 } from 'uuid'
  import useEditor from '@/composables/useEditor'
  // eslint-disable-next-line
  import CourseInfoEditor from '@/components/editor/CourseInfoEditor.vue'
  import { EditorModule } from '@/store/modules/editor'
  import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router/composables'
  import eventBus from '@/main'
  import useI18n from '@/composables/useI18n'
  import CourseInfoLoader from '@/components/loaders/CourseInfoLoader.vue'
  import CourseProgressOverview from '@/components/course/CourseProgressOverview.vue'
  import { vIntersectionObserver } from '@vueuse/components'
  import CourseExpiredBanner from '@/components/course/CourseExpiredBanner.vue'
  import CourseInfoCTA from '@/components/course/CourseInfoCTA.vue'
  import { UserModule } from '@/store/modules/user'
  import StartCourseExamCTA from '@/components/course/StartCourseExamCTA.vue'
  import { DateTime } from 'luxon'
  import CookieHelper from '@/helpers/CookieHelper'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'CourseInfo',
  setup(__props) {

  const router = useRouter()
  const route = useRoute()
  const { translateString } = useI18n()
  const { device, lesson, certificate, cardEmptyImage } = useIcons()
  const { formatDate, setIsLoadingComponentViewData, isSachkundeCompany, showCourseChangesModal, isDevCompany } =
    useCommonMixin()
  const { getPosterImage } = useVideo()
  const { isMobileDevice, isDesktopDevice, isLargeDesktop } = useBreakpoint()
  const {
    course,
    currentCourseId,
    routeToFirstCourseModule,
    progressPercentage,
    showWebinar,
    openWebinarModal,
    courseModules,
    fetchingCourseInfo,
    productPrice,
    hasPurchasedAtLeastACourse,
  } = useCourse()
  const { trackPage, pageViewed } = useSegment()
  const { inEditorMode, isEditor } = useEditor(route)

  const showCourseProgressOverview = ref(true)
  const showPackageDetails = ref(false)
  const showInfoContainer = ref(false)
  const hasCourseChanges = ref(false)
  const fetchingCourseModules = ref(true)
  const courseInfo = ref(null)
  const showAdditionalProgressOverview = ref(false)

  const courseDetails = computed(() => {
    return [
      {
        image: device.value,
        details: translateString('courses.accessOnAllDevices'),
      },
      {
        image: lesson.value,
        details: translateString('courses.totalLessonsInAllModules', {
          lessons: course.value?.numberOfLessons || 0,
          modules: course.value?.numberOfModules || 0,
        }),
      },
      {
        image: certificate.value,
        details: translateString('courses.personalCertificate'),
      },
    ]
  })

  const courseGroups = computed(() => {
    return course.value?.courseGroups.length ? course.value?.courseGroups : [{ id: '', name: '' }]
  })

  const tags = computed(() => {
    return course.value?.tags?.map((t) => t.title).join(', ')
  })

  const totalCourseGroupModules = computed(() => {
    const courseGroupsIds = course.value?.courseGroups?.map((c) => c.id) || []
    return CourseModule.totalCourseGroupModules(currentCourseId.value, courseGroupsIds)
  })

  const getCourseInfo = () => {
    if (!currentCourseId.value) return
    CourseModule.getCourse({ courseId: currentCourseId.value }).then(async (courseData: any) => {
      if (courseData?.courseBundle) {
        const bundles = (await CourseModule.getPackageBundle(
          (courseData as CourseItem).courseBundle!.courseBundleId,
        )) as CoursePackage
        if (bundles) showPackageDetails.value = !!bundles.courses?.every((c) => c.state === 'ready' && !c.purchased)
      }
    })
  }

  const createModule = () => {
    router
      .push({
        name: 'CourseModule',
        params: { courseId: currentCourseId.value, moduleId: 'mod-' + uuidv4() },
      })
      .catch(() => {
        return
      })
  }

  const createOrViewModule = () => {
    if (courseModules.value?.length) return routeToFirstCourseModule(currentCourseId.value)
    createModule()
  }

  const setDiscardAllCourseChanges = () => {
    EditorModule.setDiscardCourseChanges(true)
  }

  const setSubmitAllCourseChanges = () => {
    EditorModule.setSubmitCourseChanges(true)
  }

  //@ts-ignore
  function onIntersectionObserver([{ isIntersecting }]) {
    if (!course.value?.purchased) {
      showAdditionalProgressOverview.value = isIntersecting
    }
  }

  getCourseInfo()

  onMounted(() => {
    setIsLoadingComponentViewData(true)
    setTimeout(() => (showInfoContainer.value = true), 1500)
    trackPage('Course Details')
    pageViewed('Course Details')
  })

  const bottomPanelAnimation = ref(false)
  const overviewAnimation = ref(true)
  watch(showCourseProgressOverview, (value: boolean) => {
    if (value) {
      bottomPanelAnimation.value = false
      // wait for bottomPanelAnimation to finish
      setTimeout(() => {
        overviewAnimation.value = true
      }, 300)
    } else {
      // defer bottomPanelAnimation start to callback queue
      setTimeout(() => {
        bottomPanelAnimation.value = true
      }, 0)
      // turn off overviewAnimation only when bottomPanelAnimation is done
      setTimeout(() => {
        overviewAnimation.value = false
      }, 600)
    }
  })

  watch(currentCourseId, (value) => {
    if (value) {
      getCourseInfo()
    }
  })

  watch(fetchingCourseInfo, (value) => {
    setIsLoadingComponentViewData(value)
  })

  const changedLessonsByDate = ref<Record<string, LessonChangeLog[]>>({})
  const showWhatChangedCTA = ref(false)

  const hasWeekPassedSinceStartDate = (fixedStartDate: DateTime): boolean => {
    const now = DateTime.local().startOf('day')
    const weeksElapsed = now.diff(fixedStartDate, 'weeks').weeks
    return Math.floor(weeksElapsed) > 0
  }

  const processLessonChangesByDate = (courseModules: CourseModuleState[]): Record<string, LessonChangeLog[]> => {
    const changesByDate: Record<string, LessonChangeLog[]> = {}

    courseModules.forEach((module) => {
      module.lessons?.forEach((lesson) => {
        const lessonUpdatedAt = DateTime.fromSQL(lesson.updatedAt)
        if (lesson.type !== 'Quiz') {
          const formattedDate = lessonUpdatedAt.toFormat('dd. LLLL yyyy')

          if (!changesByDate[formattedDate]) {
            changesByDate[formattedDate] = []
          }

          changesByDate[formattedDate].push({
            courseId: currentCourseId.value,
            moduleId: module.id,
            lesson,
          })
        }
      })
    })

    return changesByDate
  }

  watch(fetchingCourseModules, (value) => {
    if (!value && UserModule.hasValidUser) {
      const dateModalViewed = CookieHelper.getCookieValue(`${course.value?.id}-course-changes-modal-viewed`)

      const fixedStartDate = dateModalViewed ? DateTime.fromSQL(dateModalViewed) : DateTime.fromSQL('2024-09-12')

      const changesByDate = processLessonChangesByDate(courseModules.value!)
      const sortedDates = Object.keys(changesByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())

      sortedDates.forEach((date) => {
        changedLessonsByDate.value[date] = changesByDate[date]
      })

      if (Object.keys(changedLessonsByDate.value).length > 0 && hasPurchasedAtLeastACourse.value) {
        showWhatChangedCTA.value = true

        if (hasWeekPassedSinceStartDate(fixedStartDate) && !isDevCompany.value) {
          showCourseChangesModal(changedLessonsByDate.value)
          CookieHelper.setCrossDomainCookie(
            `${course.value?.id}-course-changes-modal-viewed`,
            DateTime.local().toISODate(),
          )
        }
      }
    }
  })

  onBeforeRouteLeave((_to, _from, next) => {
    const { inEditorMode } = useEditor(route)
    const { translateString } = useI18n()

    if (!inEditorMode) {
      return next()
    }
    if (hasCourseChanges.value) {
      eventBus.$emit('show-modal', {
        modalContentComponent: 'ConfirmationModal',
        modalTitle: translateString('editor.thereAreChanges'),
        modalProps: {
          confirmationText: translateString('editor.saveOrDiscard'),
          cancelButtonText: translateString('editor.discardChanges'),
          okButtonText: translateString('editor.saveAllChanges'),
          showSvgs: true,
          okCallback: (closeCallback: () => void) => {
            setSubmitAllCourseChanges()
            // next()
            if (typeof closeCallback === 'function') closeCallback()
          },
          cancelCallback: () => {
            setDiscardAllCourseChanges()
            // next()
          },
        },
        cssClass: 'confirmation editor',
        modalCloseCallback: (callback: () => void) => {
          if (typeof callback === 'function') callback()
        },
      })
    } else {
      next()
    }
  })

return { __sfc: true,router, route, translateString, device, lesson, certificate, cardEmptyImage, formatDate, setIsLoadingComponentViewData, isSachkundeCompany, showCourseChangesModal, isDevCompany, getPosterImage, isMobileDevice, isDesktopDevice, isLargeDesktop, course, currentCourseId, routeToFirstCourseModule, progressPercentage, showWebinar, openWebinarModal, courseModules, fetchingCourseInfo, productPrice, hasPurchasedAtLeastACourse, trackPage, pageViewed, inEditorMode, isEditor, showCourseProgressOverview, showPackageDetails, showInfoContainer, hasCourseChanges, fetchingCourseModules, courseInfo, showAdditionalProgressOverview, courseDetails, courseGroups, tags, totalCourseGroupModules, getCourseInfo, createModule, createOrViewModule, setDiscardAllCourseChanges, setSubmitAllCourseChanges, onIntersectionObserver, bottomPanelAnimation, overviewAnimation, changedLessonsByDate, showWhatChangedCTA, hasWeekPassedSinceStartDate, processLessonChangesByDate, computed, onMounted, ref, watch, LoadingSpinner, VideoPlayer, CourseModules, CourseDescription, PlaceholderShimmer, SVGRenderer, useIcons, useCommonMixin, useCourse, useVideo, CourseModule, useSegment, TestimonialSlider, ProgressBar, CourseModuleStatusContainer, CourseModuleDuration, FeedbackPriceSection, useBreakpoint, KetchUpButton, GoogleReviewWidget, uuidv4, useEditor, CourseInfoEditor, EditorModule, onBeforeRouteLeave, useRoute, useRouter, eventBus, useI18n, CourseInfoLoader, CourseProgressOverview, vIntersectionObserver, CourseExpiredBanner, CourseInfoCTA, UserModule, StartCourseExamCTA, DateTime, CookieHelper }
}

})